/**
 * Badminton scoring rules — the single source of truth for point systems.
 *
 * This module contains every rule about how a badminton game/match is scored so
 * that the backend (result validation, winner derivation) and the frontend
 * (result entry: validation, autofill, deciding-set hiding) all agree. Nothing
 * here touches persistence or HTTP — it is pure logic.
 *
 * Supported systems:
 *  - 21-point (current BWF): games to 21, win by 2, hard cap 30 (30-29 decides).
 *  - 15-point (upcoming BWF): games to 15, win by 2, hard cap 21 (21-20 decides).
 *
 * Both are best-of-3 (first to 2 games). At the hard cap the win-by-2 rule is
 * suspended for the final point — e.g. 29-29 → 30-29 — so `cap` is the absolute
 * last point a game can reach.
 */
export var PointSystem;
(function (PointSystem) {
    /** Current BWF: games to 21, win by 2, hard cap 30 (30-29 decides). */
    PointSystem["POINTS_21"] = "21";
    /** Upcoming BWF: games to 15, win by 2, hard cap 21 (21-20 decides). */
    PointSystem["POINTS_15"] = "15";
})(PointSystem || (PointSystem = {}));
/** The system applied when a division does not specify one. */
export const DEFAULT_POINT_SYSTEM = PointSystem.POINTS_21;
export const BADMINTON_RULES = {
    [PointSystem.POINTS_21]: {
        system: PointSystem.POINTS_21,
        label: '21-point',
        target: 21,
        cap: 30,
        bestOf: 3,
        setsToWin: 2,
    },
    [PointSystem.POINTS_15]: {
        system: PointSystem.POINTS_15,
        label: '15-point',
        target: 15,
        cap: 21,
        bestOf: 3,
        setsToWin: 2,
    },
};
/** Options for a point-system selector (value + human label). */
export const POINT_SYSTEM_OPTIONS = [
    { value: PointSystem.POINTS_21, label: '21 points (best of 3) — current BWF' },
    { value: PointSystem.POINTS_15, label: '15 points (best of 3) — upcoming BWF' },
];
/** Resolve the rules for a system, falling back to the default when unset/unknown. */
export function rulesFor(system) {
    if (system && system in BADMINTON_RULES) {
        return BADMINTON_RULES[system];
    }
    return BADMINTON_RULES[DEFAULT_POINT_SYSTEM];
}
/**
 * True when `winner`-`loser` is a legal COMPLETED badminton game score under the
 * given rules (reached target, won by 2, or won at the hard cap).
 */
export function isGameWin(winner, loser, rules) {
    if (!Number.isInteger(winner) || !Number.isInteger(loser))
        return false;
    if (loser < 0 || winner <= loser)
        return false;
    if (winner > rules.cap)
        return false;
    if (winner < rules.target)
        return false;
    if (winner === rules.target) {
        // Straight win — opponent must be at least 2 behind (win by 2).
        return loser <= rules.target - 2;
    }
    if (winner === rules.cap) {
        // At the cap: a normal 2-point win (cap vs cap-2) or sudden death (cap vs cap-1).
        return loser === rules.cap - 1 || loser === rules.cap - 2;
    }
    // Between target+1 and cap-1: must be exactly a 2-point lead.
    return winner - loser === 2;
}
/**
 * Validate one completed set. Returns an error string, or null when valid.
 */
export function validateSet(a, b, rules) {
    if (!Number.isFinite(a) || !Number.isFinite(b) || a < 0 || b < 0) {
        return 'Scores cannot be negative.';
    }
    if (a > rules.cap || b > rules.cap) {
        return `Scores cannot exceed ${rules.cap}.`;
    }
    if (a === b) {
        return 'A game cannot end in a tie.';
    }
    if (!isGameWin(a, b, rules) && !isGameWin(b, a, rules)) {
        return `Invalid score ${a}–${b}: no legal ${rules.label} result (first to ${rules.target}, win by 2, cap ${rules.cap}).`;
    }
    return null;
}
/**
 * Given the LOSING score, the minimal legal WINNING score for that game — used
 * to autofill the winner cell when a captain enters the losing points. Returns
 * null if the value cannot be a losing score (it is at or above the cap).
 *
 * Examples (21-point): 10 → 21, 19 → 21, 20 → 22, 29 → 30.
 * Examples (15-point): 10 → 15, 13 → 15, 14 → 16, 20 → 21.
 */
export function winnerScoreForLoser(loser, rules) {
    if (!Number.isInteger(loser) || loser < 0)
        return null;
    if (loser >= rules.cap)
        return null; // a loser tops out at cap-1
    if (loser <= rules.target - 2)
        return rules.target; // straight win
    if (loser === rules.cap - 1)
        return rules.cap; // sudden death at the cap
    return loser + 2; // deuce
}
/** True when both scores are zero (a blank/unplayed set). */
function isBlank(s) {
    return s.a === 0 && s.b === 0;
}
/** Games won by each side across the played sets. */
export function countSetWins(sets, rules) {
    let a = 0;
    let b = 0;
    for (const s of sets) {
        if (isBlank(s))
            continue;
        if (isGameWin(s.a, s.b, rules))
            a++;
        else if (isGameWin(s.b, s.a, rules))
            b++;
    }
    return { a, b };
}
/**
 * Match winner ('a' | 'b') once a side reaches `setsToWin`, else null.
 * Stops counting at the clinching set so extra sets never change the outcome.
 */
export function deriveMatchWinner(sets, rules) {
    let a = 0;
    let b = 0;
    for (const s of sets) {
        if (isBlank(s))
            continue;
        if (isGameWin(s.a, s.b, rules))
            a++;
        else if (isGameWin(s.b, s.a, rules))
            b++;
        if (a === rules.setsToWin || b === rules.setsToWin)
            break;
    }
    if (a === rules.setsToWin)
        return 'a';
    if (b === rules.setsToWin)
        return 'b';
    return null;
}
/**
 * How many set columns the entry grid should SHOW. Always the games a match
 * needs (`setsToWin`), plus one deciding game per shared set win — so the
 * deciding game only appears while the match is still live (e.g. game 3 is
 * hidden at 2-0 in best-of-3, shown at 1-1). Capped at `bestOf`.
 */
export function visibleSetCount(sets, rules) {
    const { a, b } = countSetWins(sets, rules);
    return Math.min(rules.bestOf, rules.setsToWin + Math.min(a, b));
}
